/**
 * Roles table component for organization structure management.
 *
 * This component displays employee roles in a tabular format with their
 * associated member counts and actions. It provides functionality for
 * adding new roles through an inline button interface.
 *
 * Key Features:
 * - Displays roles with name, member count, and delete action columns
 * - Supports adding new roles via button with proper empty state
 * - Shows appropriate empty states when no roles are present
 * - Integrates with React Table for consistent table rendering
 * - Provides user-friendly interface for role management
 *
 * State Management:
 * - Empty state: Shows message and add role button when no roles exist
 * - Populated state: Shows table with data and add role row at bottom
 *
 * The component uses a "last row children" pattern to render additional UI
 * elements (empty state, add role controls) as table rows for consistent styling.
 */
import * as React from 'react';

import { Table } from '@tanstack/react-table';
import { CirclePlus, LoaderIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { DataTable } from '@/components/data-table/data-table';
import { StickyTableStateContent } from '@/components/data-table/data-table-states';
import { Button } from '@/components/ui/button';
import { RoleFormData } from '@/modules/settings/types/role';

/**
 * Props interface for the RolesTable component.
 * Includes table configuration, interaction handlers, and state flags.
 */
interface RolesTableProps {
  /** Configured React Table instance with role data and columns */
  table: Table<RoleFormData>;
  /** Callback when add role button is clicked */
  onAddRole: () => void;
  /** Whether the table currently has any roles */
  hasRoles: boolean;
  /** Whether data is currently being loaded */
  isLoading?: boolean;
}

/**
 * Roles table component with integrated role management.
 *
 * This component orchestrates the display of roles in a table format while
 * providing contextual UI for adding new roles. It adapts its presentation based
 * on current state (empty vs populated).
 *
 * Rendering Logic:
 * - Empty state: Full-height message with prominent add role button
 * - Populated state: Data table with add role row at bottom
 *
 * The component uses the DataTable's "lastRowChildren" pattern to seamlessly
 * integrate additional UI elements as table rows, maintaining consistent styling
 * and layout with the rest of the table.
 */
export const RolesTable: React.FC<RolesTableProps> = ({
  table,
  onAddRole,
  hasRoles,
  isLoading = false,
}) => {
  const { t } = useTranslation(['authenticated.settings', 'common']);

  /**
   * Renders the loading state when data is being fetched.
   * Shows a centered loading spinner within the table structure.
   */
  const renderLoadingState = (): React.ReactNode => {
    if (isLoading) {
      return (
        <tr>
          <td colSpan={table.getAllColumns().length} className="h-32 px-0">
            <StickyTableStateContent align="center" className="px-4 py-3">
              <LoaderIcon className="h-4 w-4 animate-spin text-foreground opacity-50" />
            </StickyTableStateContent>
          </td>
        </tr>
      );
    }
    return null;
  };

  /**
   * Renders the empty state when no roles are present.
   * Shows a left-aligned message with prominent add role button.
   * Only displayed when hasRoles is false and not loading.
   */
  const renderEmptyState = (): React.ReactNode => {
    if (!hasRoles && !isLoading) {
      return (
        <tr>
          <td colSpan={table.getAllColumns().length} className="px-0 py-4">
            <StickyTableStateContent className="items-start gap-3 px-8 text-left">
              <div className="flex flex-col items-start gap-0.5">
                <div className="text-sm font-semibold text-foreground">
                  {t('pages.roles.emptyState.title')}
                </div>
                <div className="text-xs text-muted-foreground">
                  {t('pages.roles.emptyState.description')}
                </div>
              </div>
              <Button type="button" onClick={onAddRole} className="h-10 gap-2">
                <CirclePlus className="h-4 w-4" />
                {t('pages.roles.addRole')}
              </Button>
            </StickyTableStateContent>
          </td>
        </tr>
      );
    }

    return null;
  };

  /**
   * Renders the add role row when roles exist.
   * Provides a compact add role interface at the bottom of the table.
   * Only shown when roles are present.
   */
  const renderAddRoleRow = (): React.ReactNode => {
    if (hasRoles) {
      return (
        <tr className="hover:bg-transparent">
          <td colSpan={table.getAllColumns().length} className="h-16 px-4">
            <div className="flex justify-start">
              <Button type="button" variant="ghost" onClick={onAddRole} className="gap-2" size="sm">
                <CirclePlus className="h-4 w-4" />
                {t('pages.roles.addRole')}
              </Button>
            </div>
          </td>
        </tr>
      );
    }

    return null;
  };

  // Combine all conditional row elements for DataTable integration
  const lastRowChildren = (
    <>
      {renderLoadingState()}
      {renderEmptyState()}
      {renderAddRoleRow()}
    </>
  );

  return <DataTable table={table} lastRowChildren={lastRowChildren} />;
};
